import { FC, useState, useEffect, useRef, useMemo, useCallback, memo } from 'react'
import { useSellerQueue } from '../hooks/useSellerQueue'
import { useQueueActions } from '../hooks/useQueueActions'
import { SellerQueueItem } from '../types'
import { moveItemUp, moveItemDown, prepareBackendUpdates } from '../utils/queueReorder'
import Swal from 'sweetalert2'
import PasswordConfirmModal from './PasswordConfirmModal'
import {i18n} from '../../../../translate/i18n'

interface QueueTabProps {
  pluginspaceId: number
  onQueueChanged: () => void
  onAttendanceStarted?: () => void
  isPrivileged: boolean
}

/**
 * QUEUE TAB - REFATORED v2
 * 
 *   FIXOS:
 * - Dependency array seguro (sem queue direto)
 * - Sincronização otimista (não revert mudanças do usuário)
 * - Fila de Swal (não múltiplos em paralelo)
 * - Proteção contra múltiplas requisições
 * - useMemo + useCallback para evitar re-renders
 */
export const QueueTab: FC<QueueTabProps> = memo(({ pluginspaceId, onQueueChanged, onAttendanceStarted, isPrivileged }) => {
  const { queue, loading } = useSellerQueue(pluginspaceId)
  const { batchReorderQueue, removeSellerFromQueue, changeStatus, callSeller, authorizeAction, startSellerAttendance } =
    useQueueActions(pluginspaceId)
  const [localQueue, setLocalQueue] = useState<SellerQueueItem[]>([])
  const [movingIndex, setMovingIndex] = useState<number | null>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)  // index being dragged
  const [swalQueue, setSwalQueue] = useState<Array<() => Promise<any>>>([])  // accepts any Promise
  const lastSyncRef = useRef<number>(0)
  const lastMovementRef = useRef<number>(0)  // marks when successful movement occurred
  const requestsInFlightRef = useRef<Set<number>>(new Set())  // Rastreia requisições em voo
  const audioContextRef = useRef<AudioContext | null>(null)  // used to play sound

  // Estado do modal de senha
  const [passwordModal, setPasswordModal] = useState<{
    isOpen: boolean
    resolve: ((value: string | null) => void) | null
    title?: string
    description?: string
  }>({ isOpen: false, resolve: null })

  // Helper para obter nome do vendedor
  const getSellerName = useCallback((item: SellerQueueItem): string => {
    return item.user?.name || item.sellerName || `Vendedor ${item.sellerId}`
  }, [])

  const translateStatus = useCallback((status: string) => {
    const normalized = status.toLowerCase()
    const key = `seller_queue.status.${normalized}`
    const translated = String(i18n.t(key))

    if (translated !== key) {
      return translated
    }

    switch (normalized) {
      case 'available':
        return 'Disponível'
      case 'in_service':
        return 'Em atendimento'
      case 'offline':
        return 'Offline'
      default:
        return status
    }
  }, [])

  // Sincroniza APENAS quando queue realmente muda (por identidade)
  // Usa useMemo para estabilizar a referência de queue
  const stableQueue = useMemo(() => {
    console.log('[QueueTab] Queue updated from server:', queue.length, 'items')
    return queue.filter((item) => item.status !== 'in_service')
  }, [queue])

  // INIT: Sincroniza com servidor - SEM LOOP
  useEffect(() => {
    // Skip se não há dados
    if (stableQueue.length === 0) {
      console.log('[QueueTab] Queue empty, skipping sync')
      return
    }

    // Skip se está movendo (não interromper operação)
    if (movingIndex !== null) {
      console.log('[QueueTab] Skipping sync - moving item at index', movingIndex)
      return
    }

    // Skip se há requisições em voo (aguardar conclusão)
    if (requestsInFlightRef.current.size > 0) {
      console.log('[QueueTab] Skipping sync - requests in flight:', requestsInFlightRef.current.size)
      return
    }

    // Skip se houve movimento recente (3s debounce após sucesso)
    const timeSinceMovement = Date.now() - lastMovementRef.current
    if (timeSinceMovement < 3000) {
      console.log('[QueueTab] Skipping sync - recent movement (' + timeSinceMovement + 'ms < 3000ms)')
      return
    }

    // Skip se sincronizou recentemente (debounce)
    const now = Date.now()
    const timeSinceSync = now - lastSyncRef.current
    if (timeSinceSync < 1000) {  // Aumentado para 1s (mais conservador)
      console.log('[QueueTab] Skipping sync - too recent (', timeSinceSync, 'ms)')
      return
    }

    // SOMENTE sincronizar se há mudanças reais
    const serverIds = stableQueue.map(q => q.id).join(',')
    const localIds = localQueue.map(q => q.id).join(',')
    
    if (serverIds === localIds && localQueue.length === stableQueue.length) {
      console.log('[QueueTab] Queue unchanged, skipping sync')
      return
    }

    console.log('[QueueTab] Syncing: server has', stableQueue.length, ', local has', localQueue.length)
    setLocalQueue([...stableQueue])
    lastSyncRef.current = now
  }, [stableQueue, movingIndex, localQueue])

  // HANDLER: Fila de Swal (evita múltiplos em paralelo)
  useEffect(() => {
    if (swalQueue.length === 0) return
    
    const showNext = async () => {
      const nextSwal = swalQueue[0]
      await nextSwal()
      setSwalQueue(prev => prev.slice(1))
    }
    
    showNext()
  }, [swalQueue])

  // SHOW_SWAL: Adiciona à fila em vez de mostrar diretamente
  const showSwalSuccess = useCallback((title: string, text: string) => {
    setSwalQueue(prev => [...prev, async () => {
      return Swal.fire({
        title,
        text,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      })
    }])
  }, [])

  const showSwalError = useCallback((title: string, text: string) => {
    setSwalQueue(prev => [...prev, async () => {
      return Swal.fire({
        title,
        text,
        icon: 'error',
      })
    }])
  }, [])

  const requestCredential = useCallback((title?: string, description?: string): Promise<string | null> => {
    return new Promise((resolve) => {
      setPasswordModal({
        isOpen: true,
        resolve,
        title: title || 'Autorização necessária',
        description: description || 'Informe a senha/PIN de gerente ou admin para movimentar a fila',
      })
    })
  }, [])

  // CONFIRMAÇÃO: Aguarda resposta
  const confirmAction = useCallback((title: string, text: string): Promise<boolean> => {
    return new Promise(resolve => {
      setSwalQueue(prev => [...prev, async () => {
        const result = await Swal.fire({
          title,
          text,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sim',
          cancelButtonText: 'Cancelar',
        })
        resolve(result.isConfirmed)
      }])
    })
  }, [])

  /**
   * HANDLER: Mover PARA CIMA
   * 
   * CRÍTICO: Usar try-catch-finally para garantir limpeza SEMPRE
   * Timeout safeguard se Promise ficar pendurada
   */
  const handleMoveUp = useCallback(async (index: number) => {
    if (!isPrivileged) {
      showSwalError('Permissão negada', 'Somente gerente/admin pode movimentar a fila manualmente')
      return
    }

    const authCredential = await requestCredential()
    if (!authCredential) {
      return
    }

    const authResult = await authorizeAction('manual_queue_move', authCredential)
    if (!authResult.authorized) {
      showSwalError('Permissão negada', authResult.message || 'Senha/PIN sem permissão')
      return
    }

    // Proteção 1: Já está movendo algo
    if (movingIndex !== null) {
      console.log('[QueueTab] Already moving index', movingIndex)
      return
    }

    // Proteção 2: Já está no topo
    if (index <= 0) {
      console.log('[QueueTab] Cannot move up - already at top')
      return
    }

    console.log(`[QueueTab] 🚀 handleMoveUp START - index: ${index}`)
    setMovingIndex(index)
    const requestId = Date.now()
    requestsInFlightRef.current.add(requestId)

    // Safeguard: Se demorar muito, força limpeza
    const timeoutId = setTimeout(() => {
      if (requestsInFlightRef.current.has(requestId)) {
        console.error('[QueueTab] 🔔 TIMEOUT SAFEGUARD - Liberando travamento após 30s')
        setMovingIndex(null)
        requestsInFlightRef.current.delete(requestId)
        showSwalError('Timeout', 'Operação demorou demais (>30s)')
      }
    }, 30000)  // 30s safeguard

    try {
      // 1️⃣ Aplicar movimento localmente
      const newQueue = moveItemUp(localQueue, index)

      // 2️⃣ Verificar se realmente mudou
      if (newQueue === localQueue) {
        console.log('[QueueTab] Move returned same array - operation invalid')
        return
      }

      // 3️⃣ Atualizar UI IMEDIATAMENTE (otimista - sem await)
      setLocalQueue(newQueue)
      console.log('[QueueTab] ✅ UI updated')

      // 4️⃣ Enviar ao backend
      const updates = prepareBackendUpdates(newQueue)
      console.log('[QueueTab] 🔄 Enviando ao backend...')
      const success = await batchReorderQueue(updates, authCredential)
      
      if (!success) {
        throw new Error('Backend rejected update')
      }

      console.log('[QueueTab] ✅✅ Backend confirmed')
      lastMovementRef.current = Date.now()  // 🆕 Marca sucesso do movimento
      showSwalSuccess('Sucesso', 'Movido para cima')
    } catch (error) {
      console.error('[QueueTab] ❌ handleMoveUp ERROR:', error)
      showSwalError('Erro', error instanceof Error ? error.message : 'Erro ao mover')
    } finally {
      clearTimeout(timeoutId)  // Cancel safeguard
      console.log('[QueueTab] 🏁 handleMoveUp CLEANUP - Liberando lock')
      setMovingIndex(null)
      requestsInFlightRef.current.delete(requestId)
      console.log('[QueueTab] ✅ handleMoveUp DONE')
    }
  }, [isPrivileged, requestCredential, authorizeAction, localQueue, movingIndex, batchReorderQueue, showSwalSuccess, showSwalError])

  /**
   * ✅ HANDLER: Mover PARA BAIXO
   * 
   * Mesmo padrão que handleMoveUp
   */
  const handleMoveDown = useCallback(async (index: number) => {
    if (!isPrivileged) {
      showSwalError('Permissão negada', 'Somente gerente/admin pode movimentar a fila manualmente')
      return
    }

    const authCredential = await requestCredential()
    if (!authCredential) {
      return
    }

    const authResult = await authorizeAction('manual_queue_move', authCredential)
    if (!authResult.authorized) {
      showSwalError('Permissão negada', authResult.message || 'Senha/PIN sem permissão')
      return
    }

    // Proteção 1: Já está movendo algo
    if (movingIndex !== null) {
      console.log('[QueueTab] Already moving index', movingIndex)
      return
    }

    // Proteção 2: Já está no final
    if (index >= localQueue.length - 1) {
      console.log('[QueueTab] Cannot move down - already at bottom')
      return
    }

    console.log(`[QueueTab] 🚀 handleMoveDown START - index: ${index}`)
    setMovingIndex(index)
    const requestId = Date.now()
    requestsInFlightRef.current.add(requestId)

    // Safeguard: Se demorar muito, força limpeza
    const timeoutId = setTimeout(() => {
      if (requestsInFlightRef.current.has(requestId)) {
        console.error('[QueueTab] 🔔 TIMEOUT SAFEGUARD - Liberando travamento após 30s')
        setMovingIndex(null)
        requestsInFlightRef.current.delete(requestId)
        showSwalError('Timeout', 'Operação demorou demais (>30s)')
      }
    }, 30000)  // 30s safeguard

    try {
      // 1️⃣ Aplicar movimento localmente
      const newQueue = moveItemDown(localQueue, index)

      // 2️⃣ Verificar se realmente mudou
      if (newQueue === localQueue) {
        console.log('[QueueTab] Move returned same array - operation invalid')
        return
      }

      // 3️⃣ Atualizar UI IMEDIATAMENTE (otimista - sem await)
      setLocalQueue(newQueue)
      console.log('[QueueTab] ✅ UI updated')

      // 4️⃣ Enviar ao backend
      const updates = prepareBackendUpdates(newQueue)
      console.log('[QueueTab] 🔄 Enviando ao backend...')
      const success = await batchReorderQueue(updates, authCredential)
      
      if (!success) {
        throw new Error('Backend rejected update')
      }

      console.log('[QueueTab] ✅✅ Backend confirmed')
      lastMovementRef.current = Date.now()  // 🆕 Marca sucesso do movimento
      showSwalSuccess('Sucesso', 'Movido para baixo')
    } catch (error) {
      console.error('[QueueTab] ❌ handleMoveDown ERROR:', error)
      showSwalError('Erro', error instanceof Error ? error.message : 'Erro ao mover')
    } finally {
      clearTimeout(timeoutId)  // Cancel safeguard
      console.log('[QueueTab] 🏁 handleMoveDown CLEANUP - Liberando lock')
      setMovingIndex(null)
      requestsInFlightRef.current.delete(requestId)
      console.log('[QueueTab] ✅ handleMoveDown DONE')
    }
  }, [isPrivileged, requestCredential, authorizeAction, localQueue, movingIndex, batchReorderQueue, showSwalSuccess, showSwalError])

  // ✅ HANDLER: Remover da fila
  const handleRemove = useCallback(async (item: SellerQueueItem) => {
    if (!isPrivileged) {
      showSwalError('Permissão negada', 'Somente gerente/admin pode remover vendedor da fila')
      return
    }

    const confirmed = await confirmAction(
      'Remover?',
      `${getSellerName(item)} será removido da fila`
    )
    
    if (!confirmed) return

    const authCredential = await requestCredential(
      'Autorização para remover',
      'Informe a senha/PIN de gerente ou admin para remover vendedor da fila'
    )

    if (!authCredential) {
      return
    }

    const authResult = await authorizeAction('manual_queue_move', authCredential)
    if (!authResult.authorized) {
      showSwalError('Permissão negada', authResult.message || 'Senha/PIN sem permissão')
      return
    }

    const requestId = Date.now()
    requestsInFlightRef.current.add(requestId)

    const timeoutId = setTimeout(() => {
      if (requestsInFlightRef.current.has(requestId)) {
        console.error('[QueueTab] 🔔 REMOVE TIMEOUT SAFEGUARD')
        requestsInFlightRef.current.delete(requestId)
        showSwalError('Timeout', 'Operação demorou demais (>30s)')
      }
    }, 30000)

    try {
      const success = await removeSellerFromQueue(item.sellerId)
      if (!success) throw new Error('Falha ao remover')

      showSwalSuccess('Removido', '')
      
      // Remover do estado local (otimista)
      setLocalQueue(prev => prev.filter(i => i.sellerId !== item.sellerId))
      
      // Notificar pai que fila mudou (trigger reload no servidor)
      onQueueChanged()
      
      // Mark recent movement para skip auto-sync por 3s
      lastMovementRef.current = Date.now()
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro ao remover';
      console.error('[QueueTab] handleRemove error:', errorMsg);
      showSwalError('Erro', errorMsg)
    } finally {
      clearTimeout(timeoutId)
      requestsInFlightRef.current.delete(requestId)
    }
  }, [isPrivileged, requestCredential, authorizeAction, removeSellerFromQueue, confirmAction, getSellerName, showSwalSuccess, showSwalError, onQueueChanged])

  // 🆕 FUNÇÃO: Reproduzir som ao mover
  const playSound = useCallback(() => {
    try {
      // Criar contexto de áudio se não existir
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      }
      
      const ctx = audioContextRef.current
      const now = ctx.currentTime
      
      // Criar oscilador para tom de sucesso (2 notas: sol-dó)
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      
      osc.connect(gain)
      gain.connect(ctx.destination)
      
      // Primeira nota: 523.25 Hz (Sol)
      osc.frequency.setValueAtTime(523.25, now)
      osc.frequency.setValueAtTime(260.87, now + 0.1)  // Depois: Dó
      
      gain.gain.setValueAtTime(0.3, now)
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2)
      
      osc.start(now)
      osc.stop(now + 0.2)
    } catch (error) {
      console.log('[QueueTab] Audio playback not available')
    }
  }, [])

  // 🆕 HANDLER: Drag Start
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer!.effectAllowed = 'move'
    e.dataTransfer!.setData('text/html', '')
    console.log('[QueueTab] 🎯 Drag started from index:', index)
  }, [])

  // 🆕 HANDLER: Drag Over
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer!.dropEffect = 'move'
  }, [])

  // 🆕 HANDLER: Drop (Soltar)
  const handleDrop = useCallback(async (e: React.DragEvent, dropIndex: number) => {
    if (!isPrivileged) {
      e.preventDefault()
      setDraggedIndex(null)
      showSwalError('Permissão negada', 'Somente gerente/admin pode arrastar para reordenar')
      return
    }

    e.preventDefault()
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      return
    }

    console.log(`[QueueTab] 🔄 Drop: from index ${draggedIndex} to ${dropIndex}`)

    const authCredential = await requestCredential()
    if (!authCredential) {
      setDraggedIndex(null)
      return
    }
    
    setDraggedIndex(null)
    setMovingIndex(dropIndex)
    
    const requestId = Date.now()
    requestsInFlightRef.current.add(requestId)

    const timeoutId = setTimeout(() => {
      if (requestsInFlightRef.current.has(requestId)) {
        console.error('[QueueTab] 🔔 DRAG DROP TIMEOUT')
        requestsInFlightRef.current.delete(requestId)
        showSwalError('Timeout', 'Operação demorou demais (>30s)')
        setMovingIndex(null)
      }
    }, 30000)

    try {
      // Reposicionar items localmente primeiro (otimista)
      const newQueue = [...localQueue]
      const [draggedItem] = newQueue.splice(draggedIndex, 1)
      newQueue.splice(dropIndex, 0, draggedItem)
      
      // Recalcular posições de todos os itens
      const reorderedQueue = newQueue.map((item, idx) => ({
        ...item,
        position: idx + 1
      }))
      
      setLocalQueue(reorderedQueue)
      console.log('[QueueTab] ✅ Local queue updated with new positions')

      // Enviar para backend
      const updates = prepareBackendUpdates(reorderedQueue)
      console.log('[QueueTab] 🔄 Enviando ao backend...')
      const success = await batchReorderQueue(updates, authCredential)
      
      if (!success) {
        throw new Error('Backend rejected update')
      }

      console.log('[QueueTab] ✅✅ Backend confirmed')
      lastMovementRef.current = Date.now()
      
      // 🆕 Tocar som após sucesso
      playSound()
      showSwalSuccess('Sucesso', 'Posição atualizada')
    } catch (error) {
      console.error('[QueueTab] ❌ handleDrop ERROR:', error)
      showSwalError('Erro', error instanceof Error ? error.message : 'Erro ao mover')
      // Reverter para fila anterior
      setLocalQueue([...stableQueue])
    } finally {
      clearTimeout(timeoutId)
      console.log('[QueueTab] 🏁 Drop CLEANUP')
      setMovingIndex(null)
      requestsInFlightRef.current.delete(requestId)
      console.log('[QueueTab] ✅ Drop DONE')
    }
  }, [isPrivileged, requestCredential, draggedIndex, localQueue, stableQueue, batchReorderQueue, showSwalSuccess, showSwalError, playSound])

  // 🆕 HANDLER: Drag End
  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null)
  }, [])

  // ✅ HANDLER: Chamar vendedor (dispara alerta no webapp)
  const handleCall = useCallback(async (item: SellerQueueItem) => {
    if (!isPrivileged) {
      showSwalError('Permissão negada', 'Somente gerente/admin pode chamar vendedor no painel')
      return
    }

    const authCredential = await requestCredential(
      'Autorização para chamar',
      'Informe a senha/PIN de gerente ou admin para chamar vendedor no painel'
    )

    if (!authCredential) {
      return
    }

    const authResult = await authorizeAction('manual_queue_move', authCredential)
    if (!authResult.authorized) {
      showSwalError('Permissão negada', authResult.message || 'Senha/PIN sem permissão')
      return
    }

    const requestId = Date.now()
    requestsInFlightRef.current.add(requestId)

    const timeoutId = setTimeout(() => {
      if (requestsInFlightRef.current.has(requestId)) {
        console.error('[QueueTab] 🔔 CALL TIMEOUT SAFEGUARD')
        requestsInFlightRef.current.delete(requestId)
        showSwalError('Timeout', 'Operação demorou demais (>20s)')
      }
    }, 20000)

    try {
      const success = await callSeller(item.id)
      if (!success) throw new Error('Falha ao chamar vendedor')

      showSwalSuccess('Chamada enviada', `${getSellerName(item)} foi chamado e recebeu alerta`)
    } catch (error) {
      showSwalError('Erro', error instanceof Error ? error.message : 'Erro')
    } finally {
      clearTimeout(timeoutId)
      requestsInFlightRef.current.delete(requestId)
    }
  }, [isPrivileged, requestCredential, authorizeAction, callSeller, getSellerName, showSwalSuccess, showSwalError])

  // ✅ HANDLER: Iniciar atendimento
  const handleStartAttendance = useCallback(async (item: SellerQueueItem) => {
    if (!isPrivileged) {
      showSwalError('Permissão negada', 'Somente gerente/admin pode iniciar atendimento no painel')
      return
    }

    const authCredential = await requestCredential(
      'Autorização para iniciar atendimento',
      'Informe a senha/PIN de gerente/admin/owner ou do vendedor selecionado para iniciar atendimento no painel'
    )

    if (!authCredential) {
      return
    }

    const authResult = await authorizeAction('return_to_queue', authCredential, item.sellerId)
    if (!authResult.authorized) {
      showSwalError('Permissão negada', authResult.message || 'Senha/PIN sem permissão')
      return
    }

    const requestId = Date.now()
    requestsInFlightRef.current.add(requestId)

    const timeoutId = setTimeout(() => {
      if (requestsInFlightRef.current.has(requestId)) {
        console.error('[QueueTab] 🔔 CALL TIMEOUT SAFEGUARD')
        requestsInFlightRef.current.delete(requestId)
        showSwalError('Timeout', 'Operação demorou demais (>20s)')
      }
    }, 20000)

    try {
      await startSellerAttendance(item.sellerId, item.position)

      setLocalQueue(prev => prev.filter(i => i.sellerId !== item.sellerId))
      onQueueChanged()
      onAttendanceStarted?.()
      showSwalSuccess('Atendimento iniciado', `${getSellerName(item)} foi movido para Em Atendimento`)
    } catch (error) {
      showSwalError('Erro', error instanceof Error ? error.message : 'Erro')
    } finally {
      clearTimeout(timeoutId)
      requestsInFlightRef.current.delete(requestId)
    }
  }, [isPrivileged, requestCredential, authorizeAction, startSellerAttendance, getSellerName, onQueueChanged, onAttendanceStarted, showSwalSuccess, showSwalError])

  if (loading && localQueue.length === 0) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Fila Atual ({localQueue.length} vendedores)</h3>
        </div>
        <div className='card-body'>
          {localQueue.length === 0 ? (
            <div className='text-center text-muted py-5'>
              <p>Nenhum vendedor na fila</p>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 gy-7'>
                <thead>
                  <tr className='fw-bold fs-6 text-gray-800'>
                    <th style={{ width: '60px' }}>Posição</th>
                    <th>Vendedor</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '260px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {localQueue.map((item, index) => (
                    <tr 
                      key={item.id}
                      draggable={isPrivileged}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={draggedIndex === index ? 'opacity-50' : ''}
                      style={{
                        cursor: 'grab',
                        backgroundColor: draggedIndex === index ? '#f0f0f0' : undefined,
                        transition: 'background-color 0.2s ease'
                      }}
                      title={isPrivileged ? 'Arraste para reordenar' : 'Sem permissão para reordenar'}
                    >
                      <td className='fw-bold text-primary' style={{ fontSize: '18px' }}>
                        {item.position}º
                      </td>
                      <td className='fw-semibold'>{getSellerName(item)}</td>
                      <td>
                        <span className='badge badge-light-success'>
                          {translateStatus(item.status)}
                        </span>
                      </td>
                      <td>
                        <div className='d-flex gap-2'>
                          {/* MOVER PARA CIMA */}
                          <button
                            type='button'
                            className='btn btn-sm btn-primary'
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0 || movingIndex !== null || !isPrivileged}
                            title='Mover para cima'
                          >
                            <i className='fas fa-chevron-up'></i>
                          </button>

                          {/* MOVER PARA BAIXO */}
                          <button
                            type='button'
                            className='btn btn-sm btn-primary'
                            onClick={() => handleMoveDown(index)}
                            disabled={index === localQueue.length - 1 || movingIndex !== null || !isPrivileged}
                            title='Mover para baixo'
                          >
                            <i className='fas fa-chevron-down'></i>
                          </button>

                          {/* CHAMAR */}
                          <button
                            type='button'
                            className='btn btn-sm btn-success'
                            onClick={() => handleCall(item)}
                            disabled={movingIndex !== null}
                            title={
                              isPrivileged
                                ? 'Chamar vendedor (envia alerta para o app do vendedor)'
                                : 'Sem permissão: apenas gerente/admin pode chamar vendedor no painel'
                            }
                          >
                            <i className='fas fa-phone'></i>
                          </button>

                          {/* INICIAR ATENDIMENTO */}
                          <button
                            type='button'
                            className='btn btn-sm btn-info'
                            onClick={() => handleStartAttendance(item)}
                            disabled={movingIndex !== null}
                            title={
                              isPrivileged
                                ? 'Iniciar atendimento (aceita senha/PIN de gerente/admin/owner ou do vendedor selecionado)'
                                : 'Sem permissão: apenas gerente/admin pode iniciar atendimento no painel'
                            }
                          >
                            <i className='fas fa-play'></i>
                          </button>

                          {/* REMOVER */}
                          <button
                            type='button'
                            className='btn btn-sm btn-danger'
                            onClick={() => handleRemove(item)}
                            disabled={movingIndex !== null}
                            title='Remover'
                          >
                            <i className='fas fa-trash'></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <PasswordConfirmModal
        isOpen={passwordModal.isOpen}
        title={passwordModal.title}
        description={passwordModal.description}
        onConfirm={(password) => {
          setPasswordModal({ isOpen: false, resolve: null })
          passwordModal.resolve?.(password)
        }}
        onCancel={() => {
          setPasswordModal({ isOpen: false, resolve: null })
          passwordModal.resolve?.(null)
        }}
      />
    </div>
  )
})
